import { convert } from 'geo-coordinates-parser';
import { MapLocation } from '../core/global';
import { downloadFile } from './core';

export const appVersion = '1.12.11';

export const isBrowser = () => typeof window !== "undefined";

export const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
    A string hashing function based on Daniel J. Bernstein's popular 'times 33' hash algorithm.
    @param {string} text - String to hash
    @return {number} Resulting number.
*/
export const hash = (text: string) => {
  let hash = 5381,
      index = text.length;

  while (index) {
      hash = (hash * 33) ^ text.charCodeAt(--index);
  }

  return hash >>> 0;
}

const MAX_LAT = 90
const MAX_LON = 180

const normaliseInput = (input: string): string =>
  `${input}`
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[\u2018\u2019\u02B9\u2032]/g, "'")
    .replace(/[\u201C\u201D\u02BA\u2033]/g, '"')
    .replace(/[\u00A0\u2007\u202F]/g, ' ')
    .trim()

const DECIMAL_PAIR =
  /^([NSEW])?\s*(-?\d+(?:\.\d+)?)\s*°?\s*([NSEW])?(?:\s*[,;/|]\s*|\s+)([NSEW])?\s*(-?\d+(?:\.\d+)?)\s*°?\s*([NSEW])?$/i

const NUMBER_TOKEN = /(^|[^\w.])(-?\d+(?:\.\d+)?)(?![\w.])/g
const LOOKS_LIKE_COORDS = /\d\.\d|\d\s*°?\s*[NSEW]\b|\b[NSEW]\s*°?\s*-?\d/i

interface SignedValue { value: number; axis: 'lat' | 'lon' | null }

const applyHemisphere = (value: number, prefix?: string, suffix?: string): SignedValue => {
  const letter = (suffix || prefix || '').toUpperCase()
  if (!letter) return { value, axis: null }
  const isLatAxis = letter === 'N' || letter === 'S'
  const isNegative = letter === 'S' || letter === 'W'
  return { value: isNegative ? -Math.abs(value) : Math.abs(value), axis: isLatAxis ? 'lat' : 'lon' }
}

const inRange = (lat: number, lon: number): boolean =>
  Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= MAX_LAT && Math.abs(lon) <= MAX_LON

const toLocation = (lat: number, lon: number): MapLocation | null =>
  inRange(lat, lon) ? { location: { lat, lng: lon } } : null

export const extractLatLon = (latLon: string): MapLocation | null => {
  const useString = normaliseInput(latLon)
  if (!useString) return null

  const decimalMatch = useString.match(DECIMAL_PAIR)
  if (decimalMatch) {
    const [, firstPrefix, firstNumber, firstSuffix, secondPrefix, secondNumber, secondSuffix] = decimalMatch
    const first = applyHemisphere(parseFloat(firstNumber), firstPrefix, firstSuffix)
    const second = applyHemisphere(parseFloat(secondNumber), secondPrefix, secondSuffix)
    const reversed = first.axis === 'lon' || second.axis === 'lat'
    const lat = reversed ? second.value : first.value
    const lon = reversed ? first.value : second.value
    const location = toLocation(lat, lon)
    if (location) return location
  }

  try {
    const converted = convert(useString, 8)
    const { decimalLatitude, decimalLongitude } = converted
    const location = toLocation(Number(decimalLatitude), Number(decimalLongitude))
    if (location) return location
  } catch (e) { /* fall through */ }

  if (LOOKS_LIKE_COORDS.test(useString)) {
    const numbers = [...useString.matchAll(NUMBER_TOKEN)].map((token) => parseFloat(token[2]))
    for (let index = 0; index < numbers.length - 1; index += 1) {
      const location = toLocation(numbers[index], numbers[index + 1])
      if (location) return location
    }
  }

  return null
}


// const useString = `${lat} ${lon}`
// // Convert DMS to decimal
// if (lat && lon && useString.match(/[NSEW]/gi) !== null) {
//   const useString = `${lat} ${lon}`
//   const dmsCoords = parseDMS(
//     useString.match(/('.)/gi) !== null ? 
//       useString.replace(/[°]/gi, ' ').replace(/('.)/gi, '.')
//     : useString);
//   useLat = dmsCoords.lat
//   useLon = dmsCoords.lon
// }

export const downloadDataAsCSV = async (data: {[key: string]: string | number | null}[], fileName: string, showDate: boolean = true): Promise<any> => {
  if (data.length > 0) {
    const [firstRow] = data
    const columnKeys: string[] = Object.keys(firstRow)
    const csvColumnKeys: string[] = [...columnKeys]
    const csvHeader = csvColumnKeys.join(',')
    const rows: string[] = []
    data.forEach(item => {
      const row: string[] = []
      csvColumnKeys.forEach(key => {
        if (key !== "" && Object.prototype.hasOwnProperty.call(item, key)) {
          const value = item[key]
          const useValue = (value === null) ? '' : `${value}`
          row.push(useValue)
        } else {
          row.push('')
        }
      })
      rows.push(row.join(','))
    })

    const csv = rows.join("\n")
    await downloadFile(`${csvHeader}\n${csv}`, fileName, showDate);
    return true
  } else {
    return false
  }
}

// export const downloadAsCSV = async (rows: (string | number)[][], fileName: string): Promise<any> => {
//   if (rows.length > 0) {
//     const csv = rows.join("\n")
//     await downloadFile(`${csv}`, fileName);
//     return true
//   } else {
//     return false
//   }
// }

type LatLon = [number, number];

type PointGroup = {
  points: LatLon[];
  centroid: LatLon;
};

const distanceDeg = (a: LatLon, b: LatLon) => {
  const dLat = a[0] - b[0];
  const dLon = a[1] - b[1];

  return Math.sqrt(dLat * dLat + dLon * dLon);
};

const calculateCentroid = (points: LatLon[]): LatLon => {
  const total = points.reduce(
    (acc, point) => {
      acc.lat += point[0];
      acc.lon += point[1];
      return acc;
    },
    { lat: 0, lon: 0 }
  );

  return [
    total.lat / points.length,
    total.lon / points.length,
  ];
};

export const groupPointsByProximity = (
  points: (LatLon | null | [null, null])[],
  maxDistanceDeg: number
): PointGroup[] => {
  const groups: PointGroup[] = [];

  points.forEach((point) => {
    if (point && point[0] !== null && point[1] !== null) {
      const matchingGroup = groups.find((group) =>
        group.points.some((existingPoint) =>
          distanceDeg(point, existingPoint) <= maxDistanceDeg
        )
      );

      if (matchingGroup) {
        matchingGroup.points.push(point);
        matchingGroup.centroid = calculateCentroid(matchingGroup.points);
      } else {
        groups.push({
          points: [point],
          centroid: point,
        });
      }
    }
    
  });

  return groups;
};
